import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
// import { Button } from 'primereact/button';

import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { calendarCategories } from 'constants/dropdownOptions';
import { userOptionTemplate } from 'modules/Tasks/components/Header/MultiSelectTemplate';
import { getUserId, bringElementToTop, bringMultiSelectElementToTop, sortByKey, isEqual } from 'utils/utils';
import { multiSelectTemplate } from 'modules/Tasks/components/TaskBoard/Board/PriorityDropdownTemplate';
import { handleFilterUpdateInUserContext, resetFilterHandler } from 'common/pagination';
import { usePracticeAreaList } from 'hooks/usePracticeAreaList';
import { LoaderComponent } from 'shared/LoaderComponent';
import { useConferenceRooms } from 'hooks/useConferenceRooms';

function CalendarFilter() {
  const {
    isGlobal,
    firmUserList,
    isClientCalendar,
    locationsList,
    typeList,
    calendarFilters,
    setCalendarFilters,
    generateFirmUserIdList,
    isFromCaseSpecificModule,
    isFromLeadSpecificModule,
    showUserFilter,
    userContext,
    isLocationLoading,
    isEventTypeLoading,
  } = useCalendarContext();

  const user_id = getUserId();
  const { practiceAreaList, isPracticeAreaLoading } = usePracticeAreaList(true, 10, 'all', true);
  const { conferenceRoomOptions, groupedConferenceRoomOptions, isConferenceRoomsLoading } = useConferenceRooms();

  const onChangeLocation = (e) => {
    let locationVal = e?.value === 'All' ? '' : e?.value || '';
    if (isGlobal) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'by_location', locationVal, true);
      // Also clear conference room filter in user context when location changes
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'conference_room', '', true);
    }
    setCalendarFilters((prevValues) => ({
      ...prevValues,
      locationFilter: locationVal,
      // Clear conference room filter when location changes
      conferenceRoomFilter: '',
    }));
  };

  const onChangeEventType = (e) => {
    let eventTypeVal = e?.value === 'All' ? '' : e?.value || '';
    if (isGlobal) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'event_type', eventTypeVal, true);
    }
    setCalendarFilters((prevValues) => ({
      ...prevValues,
      eventTypeFilter: eventTypeVal,
    }));
  };

  const onChangeCategory = (e) => {
    let categoryVal = e?.value === 'All' ? '' : e?.value || '';
    if (isGlobal) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'by_category', categoryVal, true);
    }
    setCalendarFilters((prevValues) => ({
      ...prevValues,
      categoryFilter: categoryVal,
    }));
  };

  const onChangeFirmUser = (e) => {
    let userList = [...(calendarFilters?.attendeesFilter || [])];
    if (e?.target?.name === 'multiSelect') {
      //To filter old data
      if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
        userList = [...(e.value || [])];
      } else {
        const fullList = firmUserList?.reduce((acc, curr) => [...acc, ...(curr?.items || [])], []);
        userList = [...(e.value || [])]?.filter((item) => fullList?.find((user) => isEqual(user?.assignee_id, item)));
      }
    }
    if (isGlobal) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'users', userList, true);
    }
    setCalendarFilters((prevValues) => ({
      ...prevValues,
      attendeesFilter: userList,
    }));
  };

  function resetFilter() {
    if (isGlobal) {
      resetFilterHandler(
        userContext,
        'filters',
        'calender',
        {
          by_location: '',
          event_type: '',
          by_category: '',
          by_practice_area: '',
          conference_room: '',
          users: [getUserId()],
        },
        true
      );
    }

    setCalendarFilters((prevValues) => {
      let obj = {
        ...prevValues,
        locationFilter: '',
        eventTypeFilter: '',
        categoryFilter: '',
        caseFilter: '',
        practiceAreaFilter: '',
        conferenceRoomFilter: '',
        attendeesFilter: [getUserId()],
      };

      if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
        obj['attendeesFilter'] = [...generateFirmUserIdList()];
      }
      return obj;
    });
  }

  const generateSelectedItemsLabel = () => {
    const commonSubString = 'users selected';
    const attendeesFilterLength = calendarFilters?.attendeesFilter?.length;
    const label = attendeesFilterLength === firmUserList?.length ? `All ${commonSubString}` : `${attendeesFilterLength} ${commonSubString}`;

    return label;
  };

  const generateMultiSelectedItemsLabel = () => {
    const commonSubString = 'users selected';
    const attendeesFilterLength = calendarFilters?.attendeesFilter?.length;
    const firmUserListLength = firmUserList
      .map((entry) => entry.items.length) // Get the length of items array from each entry
      .reduce((total, length) => total + length, 0); // Sum all lengths
    const label = attendeesFilterLength === firmUserListLength ? `All ${commonSubString}` : `${attendeesFilterLength} ${commonSubString}`;

    return label;
  };

  const onPracticeAreaChange = (e) => {
    let praciceAreaValue = e?.value === 'All' ? '' : e?.value || '';
    if (isGlobal) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'by_practice_area', praciceAreaValue, true);
    }
    setCalendarFilters((prevValues) => ({
      ...prevValues,
      practiceAreaFilter: praciceAreaValue,
    }));
  };

  const onConferenceRoomChange = (e) => {
    const value = e?.value === 'All' ? '' : e?.value || '';
    if (isGlobal) {
      handleFilterUpdateInUserContext(userContext, 'filters', 'calender', null, 'conference_room', value, true);
    }
    setCalendarFilters((prevValues) => ({
      ...prevValues,
      conferenceRoomFilter: value,
    }));
  };

  return (
    <div className="d-flex align-items-center flex-wrap filter-wrap px-2 py-2 py-lg-0">
      <span className="text-bold black-600 me-2 ms-1">Filters: </span>
      {!isClientCalendar && (
        <>
          <div className="d-flex flex-column me-1 my-2 r-mb-0 filter-wrap">
            <Dropdown
              placeholder="By Category"
              className="mx-1"
              options={calendarCategories}
              onChange={onChangeCategory}
              value={
                calendarFilters.categoryFilter ||
                (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_category : '')
              }
              showClear={
                !!(
                  calendarFilters.categoryFilter ||
                  (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_category : '')
                )
              }
            />
          </div>
          <div className="d-flex flex-column me-1 my-2 r-mb-0 filter-wrap">
            <Dropdown
              placeholder="By Location"
              onChange={(e) => onChangeLocation(e)}
              options={locationsList}
              value={
                calendarFilters.locationFilter ||
                (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_location : '')
              }
              className="mx-1"
              disabled={
                calendarFilters.categoryFilter === 'dob' ||
                calendarFilters.categoryFilter === 'case' ||
                calendarFilters.categoryFilter === 'task'
              }
              panelFooterTemplate={isLocationLoading ? LoaderComponent : null}
              showClear={
                !!(
                  calendarFilters.locationFilter ||
                  (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_location : '')
                )
              }
            />
          </div>

          <div className="d-flex flex-column me-1 my-2 r-mb-0 filter-wrap">
            <Dropdown
              placeholder="By Type"
              options={typeList}
              optionLabel="event_type"
              optionValue="event_type_id"
              onChange={onChangeEventType}
              value={
                calendarFilters.eventTypeFilter ||
                (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.event_type : '')
              }
              className="mx-1"
              disabled={
                calendarFilters.categoryFilter === 'dob' ||
                calendarFilters.categoryFilter === 'case' ||
                calendarFilters.categoryFilter === 'task'
              }
              panelFooterTemplate={isEventTypeLoading ? LoaderComponent : null}
              showClear={
                !!(
                  calendarFilters.eventTypeFilter ||
                  (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.event_type : '')
                )
              }
            />
          </div>
        </>
      )}

      {isGlobal && (
        <>
          <div className="d-flex flex-column me-1 my-2 r-mb-0 filter-wrap">
            <Dropdown
              placeholder="By Practice Area"
              onChange={(e) => onPracticeAreaChange(e)}
              options={practiceAreaList}
              value={
                calendarFilters.practiceAreaFilter ||
                (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_practice_area : '')
              }
              className="mx-1"
              optionValue={'practice_area_id'}
              optionLabel="practice_area_name"
              style={{ minWidth: 180 }}
              panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
              showClear={
                !!(
                  calendarFilters.practiceAreaFilter ||
                  (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_practice_area : '')
                )
              }
            />
          </div>
          <div className="d-flex flex-column me-1 my-2 r-mb-0 filter-wrap">
            {(() => {
              const selectedLocationId = calendarFilters?.locationFilter;
              let optionsToShow = groupedConferenceRoomOptions;
              let currentConferenceRoomValue =
                calendarFilters?.conferenceRoomFilter ||
                (isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.conference_room : '') ||
                '';

              if (selectedLocationId) {
                const rooms = (conferenceRoomOptions || [])
                  .filter((r) => String(r?.office_location_id) === String(selectedLocationId))
                  .map((r) => ({
                    label: r?.capacity ? `${r?.label} (${r?.capacity})` : r?.label,
                    value: r?.value,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label));
                const selectedLabel = (locationsList || []).find((l) => String(l?.value) === String(selectedLocationId))?.label;
                optionsToShow = rooms?.length ? [{ label: selectedLabel || 'Selected Location', items: rooms }] : [];

                // Check if current conference room value is valid for the selected location
                const isCurrentRoomValidForLocation = rooms.some((room) => room.value === currentConferenceRoomValue);
                if (!isCurrentRoomValidForLocation) {
                  currentConferenceRoomValue = ''; // Clear invalid conference room selection
                }
              }

              return (
                <Dropdown
                  placeholder="By Conference Room"
                  className="mx-1"
                  options={optionsToShow}
                  optionLabel="label"
                  optionValue="value"
                  optionGroupLabel="label"
                  optionGroupChildren="items"
                  panelClassName="grouped-dropdown"
                  onChange={onConferenceRoomChange}
                  value={currentConferenceRoomValue}
                  panelFooterTemplate={isConferenceRoomsLoading ? LoaderComponent : null}
                  showClear={!!currentConferenceRoomValue}
                  style={{ minWidth: 220 }}
                />
              );
            })()}
          </div>
        </>
      )}
      {/* Moved 'Show Team Calendars' button next to header actions */}

      {!isClientCalendar && (
        <div className="d-flex flex-column me-1 my-2 r-mb-0 filter-wrap">
          {(isFromCaseSpecificModule || isFromLeadSpecificModule) && (
            <MultiSelect
              value={firmUserList?.length > 0 ? calendarFilters?.attendeesFilter : []}
              options={bringElementToTop(sortByKey(firmUserList, 'first_name'), user_id)}
              onChange={onChangeFirmUser}
              optionValue={'user_id'}
              placeholder="Select Users"
              filter
              className="searchable-dropdown input-shadow"
              itemTemplate={userOptionTemplate}
              optionLabel="full_name"
              maxSelectedLabels={1}
              selectAll={true}
              name="multiSelect"
              selectedItemsLabel={generateSelectedItemsLabel()}
              resetFilterOnHide
              showClear={!!(calendarFilters?.attendeesFilter?.length > 0)}
            />
          )}
          {!isFromCaseSpecificModule && !isFromLeadSpecificModule && showUserFilter && (
            <MultiSelect
              value={firmUserList?.length > 0 ? calendarFilters?.attendeesFilter : []}
              options={bringMultiSelectElementToTop(firmUserList, user_id)}
              onChange={onChangeFirmUser}
              optionValue={'assignee_id'}
              placeholder="Select Users"
              filter
              resetFilterOnHide
              className="searchable-dropdown input-shadow"
              itemTemplate={multiSelectTemplate}
              optionLabel="assignee_name"
              optionGroupLabel="assignee_name"
              optionGroupChildren="items"
              maxSelectedLabels={1}
              selectAll={true}
              name="multiSelect"
              selectedItemsLabel={generateMultiSelectedItemsLabel()}
              showClear={!!(calendarFilters?.attendeesFilter?.length > 0)}
            />
          )}
        </div>
      )}

      {!isClientCalendar ? (
        <div className="pointer ms-0 p-2" title="Reset Filters" onClick={resetFilter}>
          <i className="fas fa-sync-alt"></i>
          <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
        </div>
      ) : null}
    </div>
  );
}

export default CalendarFilter;
